import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { resizeImage } from "@/lib/imageTools";
import { SafeImage } from "@/components/site/SafeImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search, ImagePlus } from "lucide-react";

function FieldInput({ field, value, onChange }) {
  const id = `field-${field.key}`;
  if (field.type === "textarea") {
    return <Textarea id={id} data-testid={id} rows={field.rows || 3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="bg-[#06080E] border-white/10" />;
  }
  if (field.type === "number") {
    return <Input id={id} data-testid={id} type="number" value={value ?? 0} onChange={(e) => onChange(e.target.value)} className="bg-[#06080E] border-white/10" />;
  }
  if (field.type === "select") {
    return (
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={id} className="bg-[#06080E] border-white/10 text-slate-200">
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
          {field.options.map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "switch") {
    return (
      <div className="flex items-center gap-3 h-10">
        <Switch id={id} data-testid={id} checked={!!value} onCheckedChange={onChange} />
        <span className="text-sm text-slate-400">{value ? "Yes" : "No"}</span>
      </div>
    );
  }
  if (field.type === "image") {
    const targetW = field.imageSize?.[0] || 1200;
    const targetH = field.imageSize?.[1] || 630;
    const onFile = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
        toast.error("Only PNG, JPEG or WebP images are allowed");
        return;
      }
      if (file.size > 8 * 1024 * 1024) {
        toast.error("Image must be under 8MB");
        return;
      }
      try {
        onChange(await resizeImage(file, { width: targetW, height: targetH }));
        toast.success(`Image adjusted to ${targetW}×${targetH} cover`);
      } catch {
        toast.error("Could not read that image");
      }
    };
    return (
      <div className="flex items-start gap-4">
        <label className="flex flex-col items-center justify-center w-52 rounded-lg border border-dashed border-white/20 bg-[#06080E] cursor-pointer hover:border-[#FF8800]/60 transition-colors overflow-hidden shrink-0" style={{ aspectRatio: `${targetW}/${targetH}` }} data-testid={`${id}-upload`}>
          {value ? (
            <img src={value} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <>
              <ImagePlus className="w-5 h-5 text-slate-500 mb-1" />
              <span className="text-[10px] text-slate-500 text-center px-2">PNG/JPEG/WebP<br />auto-fit to {targetW}×{targetH}</span>
            </>
          )}
          <input data-testid={`${id}-file`} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} />
        </label>
        <Input
          data-testid={`${id}-url`}
          value={value?.startsWith("data:") ? "" : value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="...or paste image URL"
          className="bg-[#06080E] border-white/10 text-xs flex-1"
        />
      </div>
    );
  }
  if (field.type === "date") {
    return <Input id={id} data-testid={id} type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="bg-[#06080E] border-white/10" />;
  }
  return <Input id={id} data-testid={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="bg-[#06080E] border-white/10" />;
}

export function CrudModule({ config }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get(config.endpoint)
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [config.endpoint]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => config.searchKeys.some((k) => String(i[k] || "").toLowerCase().includes(q)));
  }, [items, query, config.searchKeys]);

  const openForm = (item) => {
    setEditing(item || null);
    const initial = {};
    config.fields.forEach((f) => {
      initial[f.key] = item ? item[f.key] ?? f.default ?? (f.type === "switch" ? false : f.type === "number" ? 0 : "") : f.default ?? (f.type === "switch" ? false : f.type === "number" ? 0 : "");
    });
    setForm(initial);
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      config.fields.forEach((f) => {
        payload[f.key] = f.type === "number" ? Number(form[f.key]) || 0 : form[f.key];
      });
      if (editing?.id) {
        await api.put(`${config.endpoint}/${editing.id}`, payload);
        toast.success(`${config.singular} updated`);
      } else {
        await api.post(config.endpoint, payload);
        toast.success(`${config.singular} added`);
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (item) => {
    try {
      await api.put(`${config.endpoint}/${item.id}`, { ...item, published: !item.published });
      toast.success(item.published ? "Unpublished" : "Published");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${config.listTitle(item)}" permanently?`)) return;
    try {
      await api.delete(`${config.endpoint}/${item.id}`);
      toast.success(`${config.singular} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const hasPublish = config.fields.some((f) => f.key === "published");

  return (
    <div data-testid={`admin-module-${config.endpoint.split("/").pop()}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">{config.title}</h2>
          <p className="text-sm text-slate-400">{items.length} item{items.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              data-testid="admin-module-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              className="pl-9 bg-[#0D111D] border-white/10 w-48"
            />
          </div>
          <Button data-testid="admin-module-add" onClick={() => openForm(null)} className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Add {config.singular}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center text-slate-400" data-testid="admin-module-empty">No items yet.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => (
            <div key={item.id} data-testid={`admin-row-${item.id}`} className="glass rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4">
              {config.imageKey !== undefined && (
                <div className="w-full sm:w-24 h-14 rounded-lg overflow-hidden bg-[#06080E] shrink-0">
                  <SafeImage
                    src={item[config.imageKey]}
                    alt=""
                    className="w-full h-full object-cover"
                    fallback={<div className="w-full h-full flex items-center justify-center text-slate-600 text-[10px]" data-testid={`admin-thumb-fallback-${item.id}`}>No image</div>}
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display font-semibold text-white truncate">{config.listTitle(item)}</p>
                  {config.badge && (
                    <span className={`text-[10px] font-mono-tech px-2 py-0.5 rounded-full border ${config.badge(item).cls}`}>
                      {config.badge(item).text}
                    </span>
                  )}
                  {hasPublish && (
                    <span className={`text-[10px] font-mono-tech px-2 py-0.5 rounded-full border ${item.published ? "border-[#00E676]/40 text-[#00E676]" : "border-slate-500/40 text-slate-500"}`}>
                      {item.published ? "PUBLISHED" : "DRAFT"}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-1 line-clamp-1">{config.listSub(item)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {hasPublish && (
                  <Switch data-testid={`admin-publish-${item.id}`} checked={!!item.published} onCheckedChange={() => togglePublish(item)} />
                )}
                <Button size="icon" variant="outline" data-testid={`admin-edit-${item.id}`} onClick={() => openForm(item)} className="border-white/15 text-slate-300 hover:bg-white/5">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="outline" data-testid={`admin-delete-${item.id}`} onClick={() => remove(item)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="bg-[#0D111D] border-white/10 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-module-dialog">
          <DialogHeader>
            <DialogDescription className="sr-only">{editing ? `Edit ${config.singular}` : `Add ${config.singular}`}</DialogDescription>
            <DialogTitle className="font-display text-xl">{editing ? `Edit ${config.singular}` : `Add ${config.singular}`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4 mt-2">
            {config.fields.map((f) => (
              <div key={f.key} className="space-y-2">
                <Label className="text-slate-300">
                  {f.label}{f.required ? " *" : ""}
                </Label>
                <FieldInput field={f} value={form[f.key]} onChange={(v) => setForm((prev) => ({ ...prev, [f.key]: v }))} />
              </div>
            ))}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="border-white/15 text-slate-300 hover:bg-white/5">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="admin-module-save" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
